/**
 * VoiceChat.tsx — minimal in-game voice UI. Mic-mute toggle, peer
 * volume slider, audio-level VU meter, connection-quality dot.
 *
 * Lives at the top of the side panel; mounted only when both players
 * are present in the match.
 */

import clsx from 'clsx';
import type { useWebRTC } from '../hooks/useWebRTC.js';

type Props = ReturnType<typeof useWebRTC>;

const QUALITY_COLOR: Record<Props['quality'], string> = {
  excellent: 'bg-emerald-400',
  good: 'bg-emerald-500',
  poor: 'bg-amber-500',
  unknown: 'bg-cosmos-400',
};

export function VoiceChat({
  status,
  muted,
  setMuted,
  peerLevel,
  peerVolume,
  setPeerVolume,
  quality,
}: Props) {
  return (
    <div className="bg-cosmos-900 ring-1 ring-cosmos-700 rounded p-2 text-sm">
      <div className="flex items-center gap-2 mb-2">
        <span className="font-display text-xs uppercase tracking-wider text-cosmos-400">Voice</span>
        <span
          className={clsx('inline-block w-2 h-2 rounded-full', QUALITY_COLOR[quality])}
          title={`Connection: ${status}/${quality}`}
        />
        <span className="text-xs text-cosmos-400 ml-auto">{status}</span>
      </div>

      <div className="flex items-center gap-2">
        {/* Mic mute toggle */}
        <button
          type="button"
          onClick={() => setMuted(!muted)}
          className={clsx(
            'h-8 w-8 rounded-full text-base font-bold transition flex items-center justify-center',
            muted ? 'bg-rose-700 hover:bg-rose-600' : 'bg-emerald-600 hover:bg-emerald-500',
          )}
          aria-pressed={!muted}
          aria-label={muted ? 'Unmute mic' : 'Mute mic'}
        >
          {muted ? '🔇' : '🎙'}
        </button>

        {/* Peer VU meter */}
        <div className="flex-1 h-2 bg-cosmos-700 rounded overflow-hidden">
          <div
            className="h-full bg-amber-400 transition-[width] duration-75"
            style={{ width: `${Math.round(peerLevel * 100)}%` }}
          />
        </div>

        {/* Peer volume slider */}
        <input
          type="range"
          min={0}
          max={1}
          step={0.05}
          value={peerVolume}
          onChange={(e) => setPeerVolume(Number(e.target.value))}
          className="w-16"
          aria-label="Peer volume"
        />
      </div>
    </div>
  );
}
